/**
 * The display names of site roles. The token carries slugs only, so the console maps the three
 * seeded site roles (`SEEDED_SITE_ROLE_SLUGS`) to a short name and a full name. Any other slug
 * shows in sentence case with spaces, and its full name is the same text.
 */

/** The short and the full name of one seeded site role. */
interface RoleNames {
  /** The name on the chip and the segment. */
  readonly short: string;
  /** The name on hover. */
  readonly full: string;
}

/** The names of the seeded site roles, keyed by slug. */
export const SEEDED_ROLE_NAMES: Readonly<Record<string, RoleNames>> = {
  platform_admin: { short: 'Admin', full: 'Platform Admin' },
  app_developer: { short: 'Developer', full: 'App Developer' },
  fleet_operator: { short: 'Fleet operator', full: 'Fleet Operator' },
};

/** The name of a person who holds no site role. */
export const NO_SITE_ROLE_NAME = 'No site role';

/** The name of a role value that gives no text: an empty slug, or a value that is not a string. */
export const UNNAMED_ROLE_NAME = 'Unnamed role';

/** A slug in sentence case with spaces: `custom_role` gives "Custom role". */
function sentenceCase(slug: string): string {
  const words = slug.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words === '' ? UNNAMED_ROLE_NAME : words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * One name of a role. The `siteRoles` claim reaches these helpers with no check of its items, so a
 * value that is not a string gets the unnamed name and never throws.
 */
function nameOf(slug: string | null, pick: (names: RoleNames) => string): string {
  if (slug === null) return NO_SITE_ROLE_NAME;
  if (typeof slug !== 'string') return UNNAMED_ROLE_NAME;
  return Object.hasOwn(SEEDED_ROLE_NAMES, slug) ? pick(SEEDED_ROLE_NAMES[slug]) : sentenceCase(slug);
}

/** The short name of a role: "Admin", "Developer", "Fleet operator", or the slug in sentence case. */
export function roleName(slug: string | null): string {
  return nameOf(slug, (names) => names.short);
}

/** The full name of a role, for the hover text: "Platform Admin", or the slug in sentence case. */
export function roleFullName(slug: string | null): string {
  return nameOf(slug, (names) => names.full);
}
